import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate, useLocation } from "react-router-dom";
import logoImage from "@/assets/geonova-logo.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    let cachedDocHeight = document.documentElement.scrollHeight - window.innerHeight;
    
    const updateDocHeight = () => {
      cachedDocHeight = document.documentElement.scrollHeight - window.innerHeight;
    };
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollTop = window.scrollY;
          setIsScrolled(scrollTop > 20);
          
          // Use cached docHeight to avoid forced reflow
          const progress = cachedDocHeight > 0 ? (scrollTop / cachedDocHeight) * 100 : 0;
          setScrollProgress(progress);
          
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateDocHeight, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateDocHeight);
    };
  }, []);

  const scrollToSection = (id: string) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { label: t("nav.home"), id: "hero" },
    { label: t("nav.method"), id: "approach" },
    { label: t("nav.services"), id: "services" },
    { label: t("nav.cases"), id: "cases" },
    { label: "Pricing", id: "pricing" },
    { label: t("nav.contact"), id: "contact" },
    { label: t("nav.faq"), id: "faq" },
    { label: t("nav.blog"), path: "/blog" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-lg shadow-slate-200/20"
          : "bg-white/90 backdrop-blur-md border-b border-slate-100"
      }`}
    >
      {/* Scroll Progress Bar */}
      <div 
        className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-sky-400 via-blue-500 to-blue-700 transition-all duration-100 shadow-[0_0_8px_rgba(59,130,246,0.4)]" 
        style={{ width: `${scrollProgress}%` }} 
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20 gap-8">
          <div className="flex-shrink-0 -ml-2">
            <button
              onClick={() => scrollToSection("hero")}
              className="flex flex-col hover:opacity-80 transition-opacity group"
            >
              <span className="text-xl lg:text-2xl font-display font-bold flex items-center">
                <span className="text-slate-700">G</span>
                {/* Logo integrated as O */}
                <span className="relative inline-flex items-center justify-center h-8 lg:h-9 w-8 lg:w-9 -mx-[0.2rem]">
                  <img 
                    src={logoImage} 
                    alt="" 
                    className="h-full w-full object-contain filter brightness-0 invert-[.35] sepia-[.1] saturate-[0] hue-rotate-[0deg]" 
                    loading="eager"
                  />
                </span>
                <span className="text-slate-700">GENIA</span>
                <span className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">.AI</span>
              </span>
              <span className="hidden lg:block text-[11px] text-slate-500 font-medium tracking-wide -mt-1.5">
                Be the reference generative AI recommends
              </span>
            </button>
          </div>

          <nav className="hidden lg:flex items-center space-x-7">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => item.path ? handleNavigation(item.path) : scrollToSection(item.id!)}
                className="text-sm font-medium text-slate-600 hover:text-sky-500 transition-colors whitespace-nowrap"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => setLanguage(language === "fr" ? "en" : "fr")}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 hover:border-sky-400 hover:bg-sky-50 transition-all text-xs font-medium text-slate-500 hover:text-sky-600"
            >
              <Globe className="h-3.5 w-3.5" />
              <span>{language.toUpperCase()}</span>
            </button>
            <Button
              onClick={() => scrollToSection("contact")}
              size="sm"
              className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-semibold px-4 whitespace-nowrap shadow-lg shadow-sky-500/25 rounded-full"
            >
              {t("cta.audit")}
            </Button>
          </div>

          <button
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200/80">
          <nav className="container mx-auto px-4 py-4 flex flex-col space-y-4">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => item.path ? handleNavigation(item.path) : scrollToSection(item.id!)}
                className="text-sm font-medium text-slate-600 hover:text-sky-500 transition-colors text-left"
              >
                {item.label}
              </button>
            ))}
            <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1">
              <button
                onClick={() => setLanguage("fr")}
                className={`flex-1 px-3 py-2 rounded-full text-xs font-medium transition-all ${
                  language === "fr"
                    ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white"
                    : "text-slate-500"
                }`}
              >
                Français
              </button>
              <button
                onClick={() => setLanguage("en")}
                className={`flex-1 px-3 py-2 rounded-full text-xs font-medium transition-all ${
                  language === "en"
                    ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white"
                    : "text-slate-500"
                }`}
              >
                English
              </button>
            </div>
            <Button
              onClick={() => scrollToSection("contact")}
              className="bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold w-full rounded-full"
            >
              {t("cta.audit")}
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;